import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Field } from 'src/API';
import { FieldInput, FIELD_TYPES, processModuleApi } from 'src/api/process-module';
import { useSelector } from 'src/store';
import logger from 'src/log/log';

export interface CanvasFieldData {
  fieldId: string;
  fieldName: string;
  fieldLabel: string;
  canvasId: string;
  formId: string;
  formName: string;
  isRootField?: boolean;
  referencedFieldId?: string;
  templateId?: string;
}

export interface CanvasFieldState {
  mode: 'new' | 'existing';
  isLoading: boolean;
  existingFields: CanvasFieldData[];
  error: string | null;
  selectedExistingCanvas?: CanvasFieldData;
}

export interface CanvasFieldProperties {
  type: typeof FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS;
  canvasId: string;
  templateId: string;
  canvasCreated: boolean;
  override?: {
    label?: string;
  };
  access?: 'editor' | 'manager';
}

export const useProcessModuleCanvas = (
  field: FieldInput & Partial<Field>,
  moduleId: string,
  onFieldChange: (updates: Partial<FieldInput & Field>) => void,
  allFormFields?: (FieldInput & Partial<Field>)[],
) => {
  const [state, setState] = useState<CanvasFieldState>({
    mode: 'new',
    isLoading: false,
    existingFields: [],
    error: null,
    selectedExistingCanvas: undefined,
  });

  const currentTeam = useSelector((state) => state.team.current);
  const processModuleState = useSelector((state) => state.processModule);

  // Get organization ID
  const organizationId = processModuleState.processModules.list.find((pm) => pm.id === moduleId)?.managerGroups?.[0];

  // Fetch existing canvas fields
  const fetchCanvasFields = useCallback(async () => {
    if (!currentTeam?.id || !moduleId || !organizationId) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await processModuleApi.getProcessModuleCanvasFields(moduleId, organizationId);

      if (response.ok && response.result?.canvasFields) {
        // Filter out current field
        const serverCanvasFields = response.result.canvasFields.filter((canvas) => canvas.fieldId !== field.id);

        // Add locally created canvas fields that aren't saved yet
        const localCanvasFields: CanvasFieldData[] = [];
        if (allFormFields) {
          allFormFields.forEach((formField) => {
            // Check if it's a canvas field with a configured canvas that's not the current field
            if (
              formField.type === FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS &&
              formField.id !== field.id &&
              formField.properties?.canvasId &&
              formField.properties?.canvasCreated &&
              // Don't include if already exists in server results
              !serverCanvasFields.some((serverField) => serverField.fieldId === formField.id)
            ) {
              localCanvasFields.push({
                fieldId: formField.id || '',
                fieldName: formField.name || '',
                fieldLabel: formField.label || 'Canvas Field',
                canvasId: formField.properties.canvasId,
                formId: 'current-form', // Placeholder since it's not saved yet
                formName: 'Current Form', // Placeholder since it's not saved yet
              });
            }
          });
        }

        // Combine server and local canvas fields
        const allCanvasFields = [...serverCanvasFields, ...localCanvasFields];

        setState((prev) => ({
          ...prev,
          existingFields: allCanvasFields,
          isLoading: false,
        }));

        // Set mode based on current field state
        const currentCanvasId = field.properties?.canvasId;
        const existingCanvas = allCanvasFields.find((c) => c.canvasId === currentCanvasId);
        if (currentCanvasId && existingCanvas) {
          setState((prev) => ({
            ...prev,
            mode: 'existing',
            selectedExistingCanvas: existingCanvas,
          }));
        }
      } else {
        setState((prev) => ({
          ...prev,
          existingFields: [],
          isLoading: false,
          error: 'Failed to load existing canvases',
        }));
      }
    } catch (error) {
      logger.error('Failed to fetch canvas fields', error);
      setState((prev) => ({
        ...prev,
        existingFields: [],
        isLoading: false,
        error: 'Failed to load existing canvases',
      }));
    }
  }, [currentTeam?.id, moduleId, organizationId, field.id, field.properties?.canvasId, allFormFields]);

  // Initialize and fetch data
  useEffect(() => {
    fetchCanvasFields();
  }, [fetchCanvasFields]);

  // Get current canvas properties with defaults
  const getCanvasProperties = useCallback((): CanvasFieldProperties => {
    return {
      type: FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS,
      canvasId: field.properties?.canvasId || '',
      templateId: field.properties?.templateId || '',
      canvasCreated: field.properties?.canvasCreated || false,
      override: field.properties?.override || {},
      access: field.properties?.access || 'editor',
    };
  }, [field.properties]);

  // Update canvas properties
  const updateCanvasProperties = useCallback(
    (updates: Partial<CanvasFieldProperties>) => {
      const currentProps = getCanvasProperties();
      const newProperties = { ...currentProps, ...updates };

      onFieldChange({
        properties: newProperties,
      });
    },
    [getCanvasProperties, onFieldChange],
  );

  // Change canvas mode
  const setCanvasMode = useCallback(
    (mode: 'new' | 'existing') => {
      setState((prev) => ({ ...prev, mode, selectedExistingCanvas: undefined }));

      if (mode === 'new') {
        // Clear existing canvas reference but keep template
        updateCanvasProperties({
          canvasId: '',
          canvasCreated: false,
        });
        onFieldChange({
          dataSource: '',
          dataSourceType: undefined,
        });
      }
    },
    [updateCanvasProperties, onFieldChange],
  );

  // Select existing canvas
  const selectExistingCanvas = useCallback(
    (canvasId: string) => {
      const selectedCanvas = state.existingFields.find((f) => f.canvasId === canvasId);
      if (!selectedCanvas) return;

      updateCanvasProperties({
        canvasId: selectedCanvas.canvasId,
        canvasCreated: true,
      });

      onFieldChange({
        dataSource: selectedCanvas.fieldId,
        dataSourceType: 'existing_canvas',
      });

      // Store the selected existing canvas information
      setState((prev) => ({ ...prev, selectedExistingCanvas: selectedCanvas }));
    },
    [state.existingFields, updateCanvasProperties, onFieldChange],
  );

  // Configure canvas (generate canvas ID for FormBuilder)
  const configureCanvas = useCallback(() => {
    const currentProps = getCanvasProperties();
    if (!currentProps.templateId) {
      toast.error('Please select a template first');
      return false;
    }

    // Generate a canvas ID for this field
    const canvasId = `pic-field-${field.id}`;

    // Mark as configured with generated canvas ID
    updateCanvasProperties({
      canvasId: canvasId,
      canvasCreated: true,
    });

    toast.success('Canvas configured successfully!');
    return true;
  }, [getCanvasProperties, updateCanvasProperties, field.id]);

  // Validation
  const validate = useCallback(() => {
    const errors: string[] = [];
    const currentProps = getCanvasProperties();

    if (!field.label?.trim()) {
      errors.push('Canvas field must have a label');
    }

    if (state.mode === 'new') {
      if (!currentProps.templateId) {
        errors.push('Please select a template');
      }
      if (!currentProps.canvasCreated) {
        errors.push('Please configure the canvas');
      }
    } else {
      if (!currentProps.canvasId) {
        errors.push('Please select an existing canvas');
      }
    }

    return errors;
  }, [field.label, getCanvasProperties, state.mode]);

  // Computed states
  const canCreateCanvas = Boolean(
    state.mode === 'new' && getCanvasProperties().templateId && !getCanvasProperties().canvasCreated,
  );

  const hasAvailableCanvases = state.existingFields.length > 0;

  return {
    // State
    state,

    // Properties
    properties: getCanvasProperties(),

    // Actions
    setCanvasMode,
    selectExistingCanvas,
    configureCanvas,
    updateCanvasProperties,

    // Computed
    canCreateCanvas,
    hasAvailableCanvases,
    validate,

    // Loading states
    isLoading: state.isLoading,
  };
};
